/**
 * Vendor portal — Refund requests.
 * Lets the vendor view incoming refund requests and approve / deny them.
 * Approval calls the existing `store-process-refund` edge function.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { formatPrice } from "@/store/storeCart";

type RefundRow = {
  id: string;
  order_id: string;
  amount_cents: number;
  reason: string;
  buyer_notes: string | null;
  vendor_response: string | null;
  status: string;
  created_at: string;
  vendor_response_deadline: string | null;
  order: { order_number: string } | null;
};

const VendorRefunds = () => {
  const { vendor } = useVendor();
  const { toast } = useToast();
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});

  const load = async () => {
    if (!vendor) return;
    setLoading(true);
    const { data } = await supabase
      .from("store_refund_requests")
      .select(
        "id, order_id, amount_cents, reason, buyer_notes, vendor_response, status, created_at, vendor_response_deadline, order:store_orders!order_id(order_number)",
      )
      .eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false });
    setRows((data as unknown as RefundRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  const respond = async (row: RefundRow, action: "approve" | "deny") => {
    setBusy(row.id);
    const response = responses[row.id] ?? "";
    if (action === "approve") {
      const { error } = await supabase.functions.invoke("store-process-refund", {
        body: { refund_request_id: row.id, decision: "approve", vendor_response: response },
      });
      setBusy(null);
      if (error) {
        toast({ title: "Approval failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Refund approved", description: "Stripe refund initiated." });
    } else {
      const { error } = await supabase
        .from("store_refund_requests")
        .update({
          status: "denied",
          denied_at: new Date().toISOString(),
          vendor_response: response || "Denied by vendor.",
        })
        .eq("id", row.id);
      setBusy(null);
      if (error) {
        toast({ title: "Update failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Refund denied" });
    }
    void load();
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Refund requests</h2>
        <p className="text-sm text-muted-foreground">
          Buyers have 14 days to request a refund. You have 72 hours to respond before the
          request escalates.
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">No refund requests yet.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const isPending = r.status === "pending";
            return (
              <Card key={r.id} className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <Link
                      to={`/store/vendor/orders/${r.order_id}`}
                      className="font-medium hover:underline"
                    >
                      {r.order?.order_number ?? "Order"}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Requested {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="capitalize">
                      {r.status.replace(/_/g, " ")}
                    </Badge>
                    <span className="font-semibold">{formatPrice(r.amount_cents)}</span>
                  </div>
                </div>
                <div className="text-sm space-y-1">
                  <p>
                    <span className="text-muted-foreground">Reason:</span> {r.reason}
                  </p>
                  {r.buyer_notes && (
                    <p className="italic text-muted-foreground">"{r.buyer_notes}"</p>
                  )}
                  {r.vendor_response && (
                    <p className="text-xs">
                      <span className="font-medium">Your response:</span> {r.vendor_response}
                    </p>
                  )}
                </div>
                {isPending && (
                  <div className="space-y-2 pt-2 border-t">
                    <Textarea
                      rows={2}
                      placeholder="Optional response to buyer…"
                      value={responses[r.id] ?? ""}
                      onChange={(e) =>
                        setResponses((s) => ({ ...s, [r.id]: e.target.value }))
                      }
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => respond(r, "approve")}
                        disabled={busy === r.id}
                      >
                        Approve refund
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => respond(r, "deny")}
                        disabled={busy === r.id}
                      >
                        Deny
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VendorRefunds;
